import { PublicClientApplication } from "@azure/msal-browser";

const GOOGLE_SCRIPT_ID = "vbs-google-identity-script";
const APPLE_SCRIPT_ID = "vbs-apple-id-script";
const APPLE_SCRIPT_SRC = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

let googleScriptPromise = null;
let appleScriptPromise = null;
const msalClients = new Map();
const MSAL_POPUP_CALLBACK_PATH = "/msal-popup.html";

function isSafariBrowser() {
  const ua = String(window.navigator?.userAgent || "");
  return /safari/i.test(ua) && !/chrome|chromium|android|crios|fxios|edgios/i.test(ua);
}

function clearMsalInteractionState() {
  const storages = [window.sessionStorage, window.localStorage];
  for (const storage of storages) {
    try {
      const keys = [];
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key) keys.push(key);
      }
      for (const key of keys) {
        if (key === "msal.interaction.status" || key.endsWith(".interaction.status")) {
          storage.removeItem(key);
        }
      }
    } catch {
      // ignore storage access issues
    }
  }
}

function isMsalInteractionInProgressError(error) {
  const code = String(error?.errorCode || error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  return code === "interaction_in_progress" || message.includes("interaction_in_progress");
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function loadScript(src, id) {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
      // A prior failed inject can leave a dead tag; remove and retry.
      if (existing.dataset?.loaded === "1") {
        resolve();
        return;
      }
      existing.remove();
    }
    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = "1";
      resolve();
    };
    script.onerror = () => {
      script.remove();
      reject(
        new Error(
          `Failed to load ${src}. Check ad blockers / privacy extensions, or try another network.`
        )
      );
    };
    document.head.appendChild(script);
  });
}

async function ensureGoogleScript() {
  if (!googleScriptPromise) {
    googleScriptPromise = loadScript(GOOGLE_SCRIPT_SRC, GOOGLE_SCRIPT_ID).catch((error) => {
      googleScriptPromise = null;
      throw error;
    });
  }
  await googleScriptPromise;
  if (!window.google?.accounts?.oauth2?.initTokenClient) {
    throw new Error("Google Identity Services is unavailable");
  }
}

async function ensureAppleScript() {
  if (!appleScriptPromise) {
    appleScriptPromise = loadScript(APPLE_SCRIPT_SRC, APPLE_SCRIPT_ID).catch((error) => {
      appleScriptPromise = null;
      throw error;
    });
  }
  await appleScriptPromise;
  if (!window.AppleID?.auth) {
    throw new Error("Apple Sign In is unavailable");
  }
}

function msalCacheKey(clientId, tenantId, redirectUri) {
  return `${String(clientId || "")}|${String(tenantId || "")}|${String(redirectUri || "")}`;
}

function normalizeMsalRedirectUri(redirectUri) {
  const fallback = `${window.location.origin}${MSAL_POPUP_CALLBACK_PATH}`;
  const value = String(redirectUri || "").trim();
  if (!value) return fallback;
  try {
    const parsed = new URL(value, window.location.origin);
    if (parsed.host !== window.location.host) {
      return `${window.location.origin}${parsed.pathname || MSAL_POPUP_CALLBACK_PATH}`;
    }
    return parsed.toString();
  } catch {
    return fallback;
  }
}

async function getMsalClient({ clientId, tenantId, redirectUri }) {
  const safeRedirectUri = normalizeMsalRedirectUri(redirectUri);
  const key = msalCacheKey(clientId, tenantId, safeRedirectUri);
  if (!msalClients.has(key)) {
    const client = new PublicClientApplication({
      auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId || "common"}`,
        redirectUri: safeRedirectUri,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: isSafariBrowser(),
      },
      system: {
        windowHashTimeout: 120000,
      },
    });
    msalClients.set(key, client);
  }
  const client = msalClients.get(key);
  if (client?.initialize) await client.initialize();
  return client;
}

export async function getGoogleAccessToken({ clientId }) {
  if (!clientId) throw new Error("Google client id is missing");
  await ensureGoogleScript();
  return await new Promise((resolve, reject) => {
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: "openid email profile",
      prompt: "select_account",
      callback: (response) => {
        if (response?.error) {
          reject(new Error(response.error_description || response.error || "Google sign in failed"));
          return;
        }
        const accessToken = String(response?.access_token || "").trim();
        if (!accessToken) {
          reject(new Error("Google did not return an access token"));
          return;
        }
        resolve({ accessToken });
      },
    });
    tokenClient.requestAccessToken();
  });
}

export async function getAppleIdToken({ clientId, redirectURI }) {
  if (!clientId) throw new Error("Apple client id is missing");
  await ensureAppleScript();
  window.AppleID.auth.init({
    clientId,
    scope: "name email",
    redirectURI: redirectURI || `${window.location.origin}/authentication/sign-in`,
    usePopup: true,
  });
  const response = await window.AppleID.auth.signIn();
  const idToken = String(response?.authorization?.id_token || "").trim();
  if (!idToken) throw new Error("Apple did not return an id token");
  const firstName = String(response?.user?.name?.firstName || "").trim();
  const lastName = String(response?.user?.name?.lastName || "").trim();
  const name = [firstName, lastName].filter(Boolean).join(" ").trim() || null;
  const email = String(response?.user?.email || "").trim().toLowerCase();
  return { idToken, name, email: email || null };
}

export async function getMicrosoftIdToken({ clientId, tenantId, redirectUri }) {
  if (!clientId) throw new Error("Microsoft client id is missing");
  const safeRedirectUri = normalizeMsalRedirectUri(redirectUri);
  const client = await getMsalClient({ clientId, tenantId, redirectUri: safeRedirectUri });

  // Clear stale interaction markers before starting a new popup flow.
  await client.handleRedirectPromise().catch(() => null);
  clearMsalInteractionState();

  let result;
  try {
    result = await client.loginPopup({
      scopes: ["openid", "profile", "email"],
      prompt: "select_account",
      redirectUri: safeRedirectUri,
      responseMode: "fragment",
    });
  } catch (error) {
    if (!isMsalInteractionInProgressError(error)) throw error;
    clearMsalInteractionState();
    await wait(200);
    result = await client.loginPopup({
      scopes: ["openid", "profile", "email"],
      prompt: "select_account",
      redirectUri: safeRedirectUri,
      responseMode: "fragment",
    });
  }

  const idToken = String(result?.idToken || "").trim();
  if (!idToken) throw new Error("Microsoft did not return an id token");
  const account = result?.account || null;
  const name = String(account?.name || "").trim() || null;
  const email = String(account?.username || "").trim().toLowerCase() || null;
  return { idToken, name, email };
}
